// Weekly Site Reports — dashboard + list. Site Managers see "My projects" with
// Start/Continue actions; Client Admin + internal see submitted/outstanding +
// tiles/RAG/risks + 4-week trends + recurring compliance gaps (Prompt 15).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
    ClipboardCheck, ClipboardList, AlertTriangle, TrendingUp, FolderClock, ArrowRight, BellRing, ShieldAlert,
    Flag, Check, Undo2, X as XIcon, ChevronLeft, ChevronRight, EyeOff, Eye,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import KpiCard from "@/components/KpiCard";
import { fmtDate, RAG_COLORS, OVERVIEW_ITEMS } from "./wsrConstants";
import FortnightlyReportsPanel from "./FortnightlyReportsPanel";

const CAN_REVIEW_ROLES = ["client_admin", "director", "administrator"];
const RED_FLAG_LABEL = {
    red_rag: "RAG", variance: "Programme", labour: "Labour", subcontractor: "Subcontractor",
    incident: "Incident", risk_signoff: "Risk sign-off", risk_age: "Risk age",
    overdue_action: "Client action", no_report: "Report",
};

const STATUS_META = {
    submitted: { label: "Submitted", bg: "#E4F1E6", fg: "#2E7D32" },
    draft: { label: "Draft", bg: "#FDF0DC", fg: "#B5720A" },
    reviewed: { label: "Reviewed", bg: "#EAF1FB", fg: "#2A5DB0" },
    accepted: { label: "Accepted", bg: "#E4F1E6", fg: "#2E7D32" },
    returned: { label: "Returned", bg: "#FBE3E1", fg: "#B3261E" },
    no_report: { label: "Not started", bg: "#F0ECE5", fg: "#8B867E" },
};
const DOT_TONE = { green: "#2E7D32", amber: "#B5720A", red: "#B3261E", neutral: "#D8D3CB" };
const TT = { fontSize: 12, borderRadius: 8, border: "1px solid #E5E0DA", boxShadow: "0 4px 14px rgba(0,0,0,.08)" };

function addDays(iso, days) {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

function activityLabel(row) {
    if (row == null || row.days_inactive == null) return "No activity recorded";
    if (row.days_inactive <= 0) return "Today";
    if (row.days_inactive === 1) return "1 day ago";
    return `${row.days_inactive} days ago`;
}

function StatusPill({ status }) {
    const m = STATUS_META[status] || STATUS_META.no_report;
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap" style={{ background: m.bg, color: m.fg }}>{m.label}</span>;
}

// 1.0 Overview's 8 topics as coloured dots (Prompt 15A) — green/amber/red/grey, hover shows the label + status.
function OverviewDots({ flags }) {
    return (
        <div className="flex items-center gap-1" data-testid="wsr-overview-dots">
            {OVERVIEW_ITEMS.map((item) => {
                const f = (flags || {})[item.key];
                const tone = f?.tone || "neutral";
                return (
                    <span key={item.key} title={`${item.label}: ${f?.status || "No data"}`}
                          className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: DOT_TONE[tone] }} />
                );
            })}
        </div>
    );
}

function RagPill({ value }) {
    if (!value) return <span className="text-[11px] text-[#B8B2A8]">—</span>;
    const c = RAG_COLORS[value] || RAG_COLORS.Amber;
    return <span className="px-1.5 py-0.5 rounded-full text-[10.5px] font-bold" style={{ background: c.bg, color: c.fg }}>{value[0]}</span>;
}

export default function WeeklyReportsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isInternal = ["director", "administrator", "consultant"].includes(user?.role);
    const isSiteManager = user?.role === "site_manager";
    const canManageWsr = isInternal || user?.role === "client_admin"; // last-activity + exclude/re-add — the client manages their own WSR too
    const canSeeFortnightly = isInternal || user?.role === "client_admin";
    const [view, setView] = useState("weekly");

    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [projects, setProjects] = useState([]);
    const [siteId, setSiteId] = useState("");
    const [projectId, setProjectId] = useState("");
    const [weekStart, setWeekStart] = useState(null); // null = let backend pick the default week
    const [msAllWeeks, setMsAllWeeks] = useState(false); // Weekly Management Summary view only — "All weeks" in the dropdown below
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyProjectId, setBusyProjectId] = useState(null);
    const [remindingKey, setRemindingKey] = useState(null);
    const [decidingRiskId, setDecidingRiskId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const canReview = CAN_REVIEW_ROLES.includes(user?.role);

    useEffect(() => {
        if (isInternal) {
            api.get("/clients").then((r) => setClients((r.data || []).filter((c) => c.weekly_reports_enabled))).catch(() => {});
        }
    }, [isInternal]);

    // Project (+ Site) filter options — Prompt 7B.
    useEffect(() => {
        if (isSiteManager) return;
        const params = clientId ? { client_id: clientId } : {};
        api.get("/weekly-reports/projects", { params }).then((r) => setProjects(r.data || [])).catch(() => {});
    }, [clientId, isSiteManager]);
    useEffect(() => { setSiteId(""); setProjectId(""); }, [clientId]);

    const sites = useMemo(() => {
        const seen = new Map();
        projects.forEach((p) => { if (p.site_id && !seen.has(p.site_id)) seen.set(p.site_id, p.site_name); });
        return Array.from(seen, ([id, name]) => ({ id, name }));
    }, [projects]);
    const projectOptions = useMemo(
        () => (siteId ? projects.filter((p) => p.site_id === siteId) : projects),
        [projects, siteId],
    );

    const load = () => {
        setLoading(true);
        const params = {};
        if (clientId) params.client_id = clientId;
        if (projectId) params.project_id = projectId;
        if (weekStart) params.week_start = weekStart;
        api.get("/weekly-reports/dashboard", { params })
            .then((r) => {
                setData(r.data);
                if (!weekStart) setWeekStart(r.data.week_start); // sync local nav state to the backend's default, once
            })
            .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
            .finally(() => setLoading(false));
    };
    useEffect(load, [clientId, projectId, weekStart]);

    const canGoNext = !!(data?.true_current_week_start && weekStart && weekStart < data.true_current_week_start);
    const weekOptions = useMemo(() => {
        const base = data?.available_weeks || [];
        return weekStart && !base.includes(weekStart) ? [weekStart, ...base] : base;
    }, [data, weekStart]);

    const startOrOpen = async (row) => {
        if (row.report_id) { navigate(`/app/weekly-reports/${row.report_id}`); return; }
        if (!isSiteManager) return;
        setBusyProjectId(row.project_id);
        try {
            const { data: report } = await api.post("/weekly-reports", { project_id: row.project_id, week_start: data?.week_start });
            navigate(`/app/weekly-reports/${report.id}`);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setBusyProjectId(null);
        }
    };

    const remind = async (o) => {
        const key = `${o.project_id}-${o.week_start}`;
        setRemindingKey(key);
        try {
            const { data: res } = await api.post("/weekly-reports/remind", null, { params: { project_id: o.project_id, week_start: o.week_start } });
            toast.success(`Reminder sent to ${res.recipients} Site Manager${res.recipients === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setRemindingKey(null);
        }
    };

    const decideRisk = async (r, decision) => {
        setDecidingRiskId(r.risk_id);
        try {
            await api.post(`/weekly-reports/${r.report_id}/risks/${r.risk_id}/decide`, { decision });
            toast.success(decision === "approve" ? "Risk closure approved" : "Risk sent back to Open");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setDecidingRiskId(null);
        }
    };

    const toggleWsrExcluded = async (projectId, excluded) => {
        setTogglingId(projectId);
        try {
            await api.patch(`/weekly-reports/dashboard/projects/${projectId}/exclude`, { wsr_excluded: excluded });
            toast.success(excluded ? "Removed from Weekly Site Reports" : "Re-added to Weekly Site Reports");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setTogglingId(null);
        }
    };

    const m = data?.metrics;
    const trendData = useMemo(() => {
        const t = data?.trends;
        if (!t) return [];
        return t.weeks.map((w, i) => ({
            week: fmtDate(w), submission_rate: t.submission_rate[i], on_time_rate: t.on_time_rate[i],
            avg_daily_headcount: t.avg_daily_headcount[i], incidents: t.incidents[i], near_misses: t.near_misses[i],
            red_rags: t.red_rags[i],
        }));
    }, [data]);

    return (
        <div data-testid="weekly-reports-page" className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid #C75A1B", color: "#6F6A64" }}>
                        {isSiteManager ? "My Site" : "Client Reporting"}
                    </span>
                    <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#1F1F1F]">Weekly Site Reports</h2>
                    <div className="flex items-center gap-2 mt-1.5">
                        <button data-testid="wsr-week-prev" title="Previous week" disabled={!data}
                                onClick={() => setWeekStart(addDays(data.week_start, -7))}
                                className="w-6 h-6 rounded-full border border-[#E5E0DA] bg-white grid place-items-center hover:bg-[#F5F3F0] disabled:opacity-40">
                            <ChevronLeft size={13} className="text-[#6F6A64]" />
                        </button>
                        <select data-testid="wsr-week-select" value={view === "fortnightly" && msAllWeeks ? "__all__" : (data?.week_start || "")}
                                onChange={(e) => {
                                    if (e.target.value === "__all__") { setMsAllWeeks(true); return; }
                                    setMsAllWeeks(false);
                                    setWeekStart(e.target.value);
                                }}
                                className="rounded-lg border border-[#E5E0DA] bg-white px-2 py-1 text-[12.5px] text-[#1F1F1F] font-medium">
                            {view === "fortnightly" && <option value="__all__">All weeks</option>}
                            {weekOptions.map((ws) => (
                                <option key={ws} value={ws}>{`w/c ${fmtDate(ws)} – w/e ${fmtDate(addDays(ws, 6))}`}</option>
                            ))}
                        </select>
                        <button data-testid="wsr-week-next" title="Next week" disabled={!canGoNext}
                                onClick={() => setWeekStart(addDays(data.week_start, 7))}
                                className="w-6 h-6 rounded-full border border-[#E5E0DA] bg-white grid place-items-center hover:bg-[#F5F3F0] disabled:opacity-40">
                            <ChevronRight size={13} className="text-[#6F6A64]" />
                        </button>
                        {data?.week_start === data?.true_current_week_start && (
                            <span className="text-[10.5px] font-bold uppercase tracking-[.04em] text-[#C75A1B]">Current</span>
                        )}
                    </div>
                    <p className="text-[12px] mt-1" style={{ color: "#8B867E" }}>
                        {data ? `Reporting week: ${fmtDate(data.week_start)} – ${fmtDate(data.week_end)}` : "Loading…"}
                    </p>
                </div>
                {isInternal && (
                    <select data-testid="wsr-client-filter" value={clientId} onChange={(e) => setClientId(e.target.value)}
                            className="rounded-lg border border-[#E5E0DA] bg-white px-3 py-2 text-[13px]">
                        <option value="">All clients</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
                {!isSiteManager && (
                    <div className="flex items-center gap-2">
                        <select data-testid="wsr-site-filter" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                                className="rounded-lg border border-[#E5E0DA] bg-white px-3 py-2 text-[13px]">
                            <option value="">All sites</option>
                            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select data-testid="wsr-project-filter" value={projectId} onChange={(e) => setProjectId(e.target.value)}
                                className="rounded-lg border border-[#E5E0DA] bg-white px-3 py-2 text-[13px]">
                            <option value="">All projects</option>
                            {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                )}
            </div>

            {canSeeFortnightly && (
                <div className="flex items-center gap-1 bg-white rounded-full border border-[#E5E0DA] p-1 w-fit">
                    <button data-testid="wsr-view-weekly" onClick={() => setView("weekly")}
                            className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors"
                            style={view === "weekly" ? { background: "#C75A1B", color: "#fff" } : { color: "#6F6A64" }}>
                        Weekly Reports
                    </button>
                    <button data-testid="wsr-view-fortnightly" onClick={() => setView("fortnightly")}
                            className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors"
                            style={view === "fortnightly" ? { background: "#C75A1B", color: "#fff" } : { color: "#6F6A64" }}>
                        Weekly Management Summary
                    </button>
                </div>
            )}

            {view === "fortnightly" ? (
                <FortnightlyReportsPanel clientId={clientId} projectId={projectId} siteId={siteId}
                                         weekStart={msAllWeeks ? null : (data?.week_start || weekStart)} />
            ) : (
            <>

            {loading && <div className="font-serif-italic text-[#9A968F] py-10 text-center">Loading…</div>}

            {!loading && data && (
                <>
                    <div className={`grid grid-cols-2 gap-3 ${isSiteManager ? "sm:grid-cols-4" : "sm:grid-cols-5"}`}>
                        <KpiCard icon={ClipboardCheck} label="Submitted this week" value={m.submitted_this_week} tone="green" kpiKey="wsr-submitted" />
                        <KpiCard icon={FolderClock} label="Outstanding" value={m.outstanding_count} tone="amber" kpiKey="wsr-outstanding" />
                        <KpiCard icon={ClipboardList} label="Active projects" value={m.total_projects} tone="neutral" kpiKey="wsr-projects" />
                        <KpiCard icon={TrendingUp} label="4-week submission rate" value={`${m.submission_rate_4wk}%`} tone="accent" kpiKey="wsr-rate" />
                        {!isSiteManager && (
                            <KpiCard icon={ShieldAlert} label="Risks awaiting sign-off" value={m.risks_awaiting_signoff_count} tone="red" kpiKey="wsr-risks-awaiting" />
                        )}
                    </div>

                    {!isSiteManager && (data.red_flags || []).length > 0 && (
                        <div data-testid="wsr-red-flags-panel" className="bg-white rounded-2xl border border-[#F3C6C2] shadow-brand-sm overflow-hidden">
                            <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#B3261E] flex items-center gap-2">
                                <Flag size={16} /> Red flags
                            </div>
                            <div className="p-3 flex flex-col gap-1.5 max-h-72 overflow-y-auto">
                                {data.red_flags.map((f, i) => (
                                    <button key={i} data-testid={`wsr-red-flag-${i}`}
                                            onClick={() => f.link && navigate(f.link)}
                                            className="text-left flex items-center justify-between gap-2 text-[12.5px] px-3 py-2 rounded-lg bg-[#FBE3E1]/40 hover:bg-[#FBE3E1]/70">
                                        <span className="text-[#8A2A21]"><span className="font-semibold">{f.project_name}</span> — {f.message}</span>
                                        <span className="text-[10px] font-bold uppercase tracking-[.04em] text-[#B3261E] shrink-0">{RED_FLAG_LABEL[f.type] || f.type}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {data.project_detail && (
                        <div data-testid="wsr-project-detail-panel" className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
                            <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#1F1F1F]">
                                {data.project_detail.project_name} — last 8 weeks
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-[13px]">
                                    <thead>
                                        <tr className="bg-[#F5F3F0] text-left">
                                            <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Week ending</th>
                                            <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Status</th>
                                            <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Time / Cost / Info</th>
                                            <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Variance</th>
                                            <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] text-right">Headcount</th>
                                            <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] text-right">Open risks</th>
                                            <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] text-right">Client actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.project_detail.weeks.map((w) => (
                                            <tr key={w.week_start} data-testid={`wsr-detail-week-${w.week_start}`}
                                                className="border-t border-[#F0ECE5] cursor-pointer hover:bg-[#FBF3EC]"
                                                onClick={() => w.report_id && navigate(`/app/weekly-reports/${w.report_id}`)}>
                                                <td className="px-4 py-2 text-[#1F1F1F] font-medium">{fmtDate(w.week_start)}</td>
                                                <td className="px-4 py-2"><StatusPill status={w.status} /></td>
                                                <td className="px-4 py-2">
                                                    <div className="flex items-center gap-1">
                                                        <RagPill value={w.rag?.time} /><RagPill value={w.rag?.cost} /><RagPill value={w.rag?.information} />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2" style={{ color: w.variance_pts != null && w.variance_pts < -15 ? "#B3261E" : "#6F6A64" }}>
                                                    {w.variance_pts != null ? `${w.variance_pts > 0 ? "+" : ""}${w.variance_pts.toFixed(1)} pts` : "—"}
                                                </td>
                                                <td className="px-4 py-2 text-right">{w.headcount || "—"}</td>
                                                <td className="px-4 py-2 text-right">{w.open_risks}</td>
                                                <td className="px-4 py-2 text-right">{w.open_client_actions}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
                        <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#1F1F1F]">
                            {data?.week_start === data?.true_current_week_start ? "This week's reports" : `Reports — w/e ${fmtDate(data?.week_end)}`}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr className="bg-[#F5F3F0] text-left">
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Project</th>
                                        {data.is_all_clients && <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Client</th>}
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Status</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Overview</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Time / Cost / Info</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] text-right">High risks</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] text-right">Client actions</th>
                                        {canManageWsr && <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Last activity</th>}
                                        <th className="px-4 py-2 w-32" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.projects || []).length === 0 && (
                                        <tr><td colSpan={9} className="px-4 py-4 text-[#8B867E] italic">No active projects require a weekly report.</td></tr>
                                    )}
                                    {(data.projects || []).map((row) => {
                                        const hoverTip = [row.delay_cause && `Delay cause: ${row.delay_cause}`, row.recovery_actions && `Recovery: ${row.recovery_actions}`].filter(Boolean).join(" · ");
                                        return (
                                        <tr key={row.project_id} data-testid={`wsr-project-row-${row.project_id}`} className="border-t border-[#F0ECE5]">
                                            <td className="px-4 py-2.5 font-medium text-[#1F1F1F]">
                                                {row.project_name}
                                                {row.status !== "accepted" && row.status !== "reviewed" && (
                                                    <div data-testid={`wsr-project-due-${row.project_id}`} className="text-[10.5px] font-normal text-[#8B867E]">{row.due_label}</div>
                                                )}
                                            </td>
                                            {data.is_all_clients && <td className="px-4 py-2.5 text-[#6F6A64]">{row.client_name}</td>}
                                            <td className="px-4 py-2.5"><StatusPill status={row.status} /></td>
                                            <td className="px-4 py-2.5"><OverviewDots flags={row.overview_flags} /></td>
                                            <td className="px-4 py-2.5" title={hoverTip || undefined}>
                                                <div className="flex items-center gap-1.5">
                                                    <RagPill value={row.rag?.time} /><RagPill value={row.rag?.cost} /><RagPill value={row.rag?.information} />
                                                    {row.programme_variance_pts != null && (
                                                        <span data-testid={`wsr-variance-${row.project_id}`}
                                                              className="text-[10.5px] font-semibold"
                                                              style={{ color: row.programme_variance_pts < -15 ? "#B3261E" : "#8B867E" }}>
                                                            {row.programme_variance_pts > 0 ? "+" : ""}{row.programme_variance_pts.toFixed(1)}pts
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-2.5 text-right">
                                                {row.high_risks_count > 0 ? (
                                                    <span className="inline-flex items-center gap-1 text-[#B3261E] font-semibold"><ShieldAlert size={12} />{row.high_risks_count}</span>
                                                ) : <span className="text-[#B8B2A8]">0</span>}
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-[#1F1F1F] font-medium">{row.open_client_actions_count}</td>
                                            {canManageWsr && (
                                                <td className="px-4 py-2.5">
                                                    <span data-testid={`wsr-last-activity-${row.project_id}`}
                                                          className="text-[12px]" style={{ color: (row.days_inactive == null || row.days_inactive > 30) ? "#B3261E" : "#6F6A64" }}>
                                                        {activityLabel(row)}
                                                    </span>
                                                </td>
                                            )}
                                            <td className="px-4 py-2.5 text-right">
                                                <div className="flex items-center justify-end gap-2.5">
                                                    {row.status === "no_report" && !isSiteManager ? (
                                                        <span className="text-[12px] text-[#8B867E]">—</span>
                                                    ) : (
                                                        <button data-testid={`wsr-project-action-${row.project_id}`}
                                                                disabled={busyProjectId === row.project_id}
                                                                onClick={() => startOrOpen(row)}
                                                                className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#C75A1B] hover:underline disabled:opacity-50">
                                                            {row.report_id ? (["draft", "returned"].includes(row.status) ? "Continue" : "View") : "Start report"}
                                                            <ArrowRight size={13} />
                                                        </button>
                                                    )}
                                                    {canManageWsr && (
                                                        <button data-testid={`wsr-remove-${row.project_id}`} title="Remove this site from Weekly Site Reports"
                                                                disabled={togglingId === row.project_id}
                                                                onClick={() => toggleWsrExcluded(row.project_id, true)}
                                                                className="text-[#B8B2A8] hover:text-[#B3261E] disabled:opacity-50">
                                                            <EyeOff size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {canManageWsr && (data.excluded_projects || []).length > 0 && (
                        <div data-testid="wsr-excluded-panel" className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
                            <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#1F1F1F] flex items-center gap-2">
                                <EyeOff size={16} className="text-[#8B867E]" /> Excluded from Weekly Site Reports ({data.excluded_projects.length})
                            </div>
                            <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                                {data.excluded_projects.map((p) => (
                                    <div key={p.project_id} data-testid={`wsr-excluded-row-${p.project_id}`}
                                         className="flex items-center justify-between text-[13px] px-3 py-2 rounded-lg bg-[#F5F3F0] gap-2">
                                        <div className="min-w-0">
                                            <div className="font-medium text-[#1F1F1F] truncate">
                                                {p.project_name}{data.is_all_clients && <span className="text-[#8B867E]"> · {p.client_name}</span>}
                                            </div>
                                            <div className="text-[11.5px] text-[#8B867E]">Last activity {activityLabel(p)}</div>
                                        </div>
                                        <button data-testid={`wsr-readd-${p.project_id}`} disabled={togglingId === p.project_id}
                                                onClick={() => toggleWsrExcluded(p.project_id, false)}
                                                className="inline-flex items-center gap-1 shrink-0 text-[11.5px] font-semibold text-[#C75A1B] hover:underline disabled:opacity-50">
                                            <Eye size={12} /> Re-add
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!isSiteManager && (
                        <div className="grid lg:grid-cols-2 gap-4">
                            <TrendCard title="Submission & on-time rate" subtitle="Last 4 weeks · submitted by Friday 23:59 London counts as on-time">
                                <LineChart data={trendData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                    <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} unit="%" />
                                    <Tooltip contentStyle={TT} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="submission_rate" name="Submission rate" stroke="#C75A1B" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="on_time_rate" name="On-time rate" stroke="#2A5DB0" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </TrendCard>
                            <TrendCard title="Average daily headcount" subtitle="Across all resource registers, last 4 weeks">
                                <BarChart data={trendData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                    <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={TT} cursor={{ fill: "#FBEFE5" }} />
                                    <Bar dataKey="avg_daily_headcount" name="Avg daily headcount" fill="#C75A1B" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </TrendCard>
                            <TrendCard title="Incidents & near misses" subtitle="Last 4 weeks">
                                <LineChart data={trendData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                    <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <Tooltip contentStyle={TT} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="incidents" name="Incidents" stroke="#B3261E" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="near_misses" name="Near misses" stroke="#B5720A" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </TrendCard>
                            <TrendCard title="Red RAGs" subtitle="Projects with a Time, Cost or Information RAG at Red — last 4 weeks">
                                <BarChart data={trendData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                    <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <Tooltip contentStyle={TT} cursor={{ fill: "#FBEFE5" }} />
                                    <Bar dataKey="red_rags" name="Red RAGs" fill="#B3261E" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </TrendCard>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
                            <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#1F1F1F] flex items-center gap-2">
                                <FolderClock size={16} className="text-[#B5720A]" /> Outstanding
                            </div>
                            <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                                {(data.outstanding || []).length === 0 && <div className="text-[13px] text-[#8B867E] italic">Nothing outstanding — great work.</div>}
                                {(data.outstanding || []).map((o, i) => {
                                    const key = `${o.project_id}-${o.week_start}`;
                                    return (
                                        <div key={key} data-testid={`wsr-outstanding-${i}`} className="flex items-center justify-between text-[13px] px-3 py-2 rounded-lg bg-[#FDF0DC]/60 gap-2">
                                            <div className="min-w-0">
                                                <div className="font-medium text-[#1F1F1F] truncate">{o.project_name}</div>
                                                <div className="text-[#B5720A] text-[11.5px]">w/e {fmtDate(o.week_start)}</div>
                                            </div>
                                            {!isSiteManager && (
                                                <button data-testid={`wsr-remind-${i}`} disabled={remindingKey === key} onClick={() => remind(o)}
                                                        className="inline-flex items-center gap-1 shrink-0 text-[11.5px] font-semibold text-[#C75A1B] hover:underline disabled:opacity-50">
                                                    <BellRing size={12} /> {remindingKey === key ? "Sending…" : "Remind"}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
                            <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#1F1F1F] flex items-center gap-2">
                                <AlertTriangle size={16} className="text-[#B33A2E]" /> Recurring compliance gaps
                            </div>
                            <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                                {(data.compliance_gaps || []).length === 0 && <div className="text-[13px] text-[#8B867E] italic">No repeat "No" checklist answers in the last 4 weeks.</div>}
                                {(data.compliance_gaps || []).map((g, i) => (
                                    <div key={i} data-testid={`wsr-gap-${i}`} className="flex items-center justify-between text-[13px] px-3 py-2 rounded-lg bg-[#FBE3E1]/40">
                                        <div>
                                            <div className="font-medium text-[#1F1F1F]">{g.item}</div>
                                            <div className="text-[11.5px] text-[#8B867E]">{g.section}</div>
                                        </div>
                                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#B3261E] text-white">{g.count}×</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {canReview && (data.risks_awaiting_signoff || []).length > 0 && (
                        <div data-testid="wsr-risks-awaiting-panel" className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
                            <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#1F1F1F] flex items-center gap-2">
                                <ShieldAlert size={16} className="text-[#B5720A]" /> Risks awaiting sign-off
                            </div>
                            <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                                {data.risks_awaiting_signoff.map((r) => (
                                    <div key={r.risk_id} data-testid={`wsr-awaiting-${r.risk_id}`} className="rounded-lg bg-[#FDF0DC]/50 px-3 py-2.5 space-y-1.5">
                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                            <div className="min-w-0">
                                                <div className="text-[13px] font-semibold text-[#1F1F1F]">{r.ref} · {r.project_name}</div>
                                                <div className="text-[12px] text-[#6F6A64]">{r.risk}</div>
                                                {r.closure_note && <div className="text-[11.5px] text-[#8B867E] italic">"{r.closure_note}"</div>}
                                                {r.age_days != null && <div className="text-[10.5px] text-[#B5720A]">Open {r.age_days} days</div>}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button data-testid={`wsr-awaiting-approve-${r.risk_id}`} disabled={decidingRiskId === r.risk_id}
                                                        onClick={() => decideRisk(r, "approve")}
                                                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#2E7D32] hover:underline disabled:opacity-50">
                                                    <Check size={13} /> Approve
                                                </button>
                                                <button data-testid={`wsr-awaiting-reject-${r.risk_id}`} disabled={decidingRiskId === r.risk_id}
                                                        onClick={() => decideRisk(r, "reject")}
                                                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#B3261E] hover:underline disabled:opacity-50">
                                                    <Undo2 size={13} /> Reopen
                                                </button>
                                                <button data-testid={`wsr-awaiting-open-${r.risk_id}`} onClick={() => navigate(`/app/weekly-reports/${r.report_id}`)}
                                                        className="text-[11.5px] font-semibold text-[#C75A1B] hover:underline">Open</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
                        <div className="px-5 py-3 border-b border-[#F0ECE5] font-bold text-[14px] text-[#1F1F1F]">Recently submitted</div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr className="bg-[#F5F3F0] text-left">
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Project</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Report #</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Week ending</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Status</th>
                                        <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Risk summary</th>
                                        <th className="px-4 py-2 w-16" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.recent_submitted || []).length === 0 && (
                                        <tr><td colSpan={6} className="px-4 py-4 text-[#8B867E] italic">Nothing submitted yet.</td></tr>
                                    )}
                                    {(data.recent_submitted || []).map((r) => (
                                        <tr key={r.id} data-testid={`wsr-recent-${r.id}`} className="border-t border-[#F0ECE5] align-top">
                                            <td className="px-4 py-2.5 font-medium text-[#1F1F1F]">{r.project_name}</td>
                                            <td className="px-4 py-2.5 text-[#6F6A64]">{r.report_number}</td>
                                            <td className="px-4 py-2.5 text-[#6F6A64]">{fmtDate(r.week_end)}</td>
                                            <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                                            <td className="px-4 py-2.5">
                                                {(r.risk_summary || []).length === 0 ? (
                                                    <span className="text-[#B8B2A8]">—</span>
                                                ) : (
                                                    <div className="flex flex-col gap-1 max-w-sm">
                                                        {r.risk_summary.slice(0, 3).map((rk, i) => (
                                                            <div key={i} className="text-[11.5px] text-[#6F6A64] truncate">
                                                                {rk.ref && <span className="font-semibold text-[#1F1F1F]">{rk.ref} · </span>}
                                                                {rk.risk} {rk.impact && <span className="text-[#B5720A]">({rk.impact})</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5 text-right">
                                                <button onClick={() => navigate(`/app/weekly-reports/${r.id}`)}
                                                        className="text-[12.5px] font-semibold text-[#C75A1B] hover:underline">View</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
            </>
            )}
        </div>
    );
}

function TrendCard({ title, subtitle, children }) {
    return (
        <div className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm p-4">
            <div className="mb-2">
                <div className="text-[13.5px] font-bold text-[#1F1F1F]">{title}</div>
                {subtitle && <div className="text-[11.5px] text-[#8B867E]">{subtitle}</div>}
            </div>
            <ResponsiveContainer width="100%" height={200}>
                {children}
            </ResponsiveContainer>
        </div>
    );
}
